import { useEffect, useState, type FormEvent } from 'react'
import {
  Plus,
  Trash2,
  Loader2,
  CircleCheck,
  CircleSlash,
} from 'lucide-react'
import {
  ProductKindLabel,
  ProductUnitLabel,
  type ProductDto,
  type ProductKindT,
  type ProductUnitT,
} from '@jr2os/shared'
import { useTranslation } from 'react-i18next'
import { api, ApiError } from '../../lib/api'
import { confirmDialog, toast } from '../../lib/dialogs'
import { useServiceLines } from '../../lib/service-lines'
import Modal from '../../components/ui/Modal'
import {
  formatCents,
  parseEurosToCents,
  centsToEurosInput,
} from '../../lib/money'
import { useTableSort } from '../../lib/useTableSort'

const KIND_VALUES = Object.keys(ProductKindLabel) as ProductKindT[]
const UNIT_VALUES = Object.keys(ProductUnitLabel) as ProductUnitT[]
const VAT_OPTIONS: { value: number; label: string }[] = [
  { value: 0, label: '0%' },
  { value: 400, label: '4%' },
  { value: 1000, label: '10%' },
  { value: 2100, label: '21%' },
]
const IRPF_OPTIONS: { value: number; label: string }[] = [
  { value: 0, label: '0%' },
  { value: 700, label: '7%' },
  { value: 1500, label: '15%' },
]

const PRODUCT_CODE_REGEX = '^[A-Z0-9-]+$'

function formatBps(bps: number): string {
  return `${bps / 100}%`
}

export default function Products() {
  const { t } = useTranslation(['settings', 'common'])
  const { nameOf, allLines } = useServiceLines()
  const [items, setItems] = useState<ProductDto[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [includeInactive, setIncludeInactive] = useState(false)
  const [showCreate, setShowCreate] = useState(false)
  const [editing, setEditing] = useState<ProductDto | null>(null)

  // Orden por columna, solo en memoria: esta pantalla es una pestaña de Catálogos y la URL no guarda la pestaña.
  // La línea de servicio se ordena por su NOMBRE (que sale de otra lista, de ahí `deps`).
  const { rows: sortedItems, header } = useTableSort(
    items,
    {
      code: (p) => p.code,
      name: (p) => p.name,
      kind: (p) => t('enums:ProductKind.' + p.kind),
      unit: (p) => t('enums:ProductUnit.' + p.unit),
      price: (p) => p.unitPriceCents,
      vat: (p) => p.vatRateBps,
      irpf: (p) => p.irpfRateBps,
      serviceLine: (p) => (p.serviceLine ? nameOf(p.serviceLine) : null),
      status: (p) => (p.active ? t('products.status.active') : t('products.status.inactive')),
    },
    { persist: 'state', deps: [allLines] }
  )

  const reload = async () => {
    setError(null)
    try {
      const path = includeInactive ? '/products?includeInactive=true' : '/products'
      const { items } = await api.get<{ items: ProductDto[] }>(path)
      setItems(items)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('products.errors.load'))
    }
  }

  useEffect(() => {
    void reload()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeInactive])

  const onDelete = async (p: ProductDto, evt: React.MouseEvent) => {
    evt.stopPropagation()
    if (
      !(await confirmDialog({
        message: t('products.confirmDelete', { name: p.name }),
        tone: 'danger',
      }))
    )
      return
    try {
      await api.delete(`/products/${p.id}`)
      await reload()
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string } | undefined
        toast.error(body?.error ?? err.message)
      } else {
        toast.error(err instanceof Error ? err.message : t('common:errors.generic'))
      }
    }
  }

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-2xl font-serif font-semibold t-primary">
            {t('products.title')}
          </h2>
          <p className="t-muted text-sm mt-1">
            {t('products.subtitle')}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="inline-flex items-center gap-2 text-sm t-secondary cursor-pointer select-none">
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
              className="w-4 h-4 rounded accent-jr2-500 focus:ring-jr2-500/40"
            />
            {t('products.includeInactive')}
          </label>
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            className="px-3.5 py-2 rounded-lg text-sm font-semibold text-white bg-jr2-600 hover:bg-jr2-700 hover:shadow-glow-jr2 transition-all flex items-center gap-1.5"
          >
            <Plus size={16} /> {t('products.newButton')}
          </button>
        </div>
      </header>

      {error && (
        <div className="text-sm rounded-lg px-3 py-2 border text-rose-700 dark:text-rose-300 bg-rose-500/10 border-rose-500/30">
          {error}
        </div>
      )}

      <div className="surface overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider t-faint border-b border-base">
              {header('code', t('products.table.code'), { className: 'px-4 py-3 font-medium' })}
              {header('name', t('products.table.name'), { className: 'px-4 py-3 font-medium' })}
              {header('kind', t('products.table.kind'), { className: 'px-4 py-3 font-medium' })}
              {header('unit', t('products.table.unit'), { className: 'px-4 py-3 font-medium' })}
              {header('price', t('products.table.price'), { align: 'right', className: 'px-4 py-3 font-medium' })}
              {header('vat', t('products.table.vat'), { align: 'right', className: 'px-4 py-3 font-medium' })}
              {header('irpf', t('products.table.irpf'), { align: 'right', className: 'px-4 py-3 font-medium' })}
              {header('serviceLine', t('products.table.serviceLine'), { className: 'px-4 py-3 font-medium' })}
              {header('status', t('products.table.status'), { className: 'px-4 py-3 font-medium' })}
              <th className="px-4 py-3 font-medium text-right">{t('products.table.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {items === null ? (
              <tr>
                <td colSpan={10} className="px-4 py-10 text-center t-muted">
                  <Loader2 size={18} className="inline animate-spin mr-2" /> {t('common:loading')}
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={10} className="px-4 py-10 text-center t-muted">
                  {t('products.empty')}
                </td>
              </tr>
            ) : (
              sortedItems.map((p) => (
                <tr
                  key={p.id}
                  onClick={() => setEditing(p)}
                  className="border-b border-base/50 last:border-b-0 hover:bg-hover/50 cursor-pointer"
                >
                  <td className="px-4 py-3 t-primary font-mono text-xs">{p.code}</td>
                  <td className="px-4 py-3 t-primary font-medium">{p.name}</td>
                  <td className="px-4 py-3 t-secondary text-xs">
                    {t('enums:ProductKind.' + p.kind)}
                  </td>
                  <td className="px-4 py-3 t-secondary text-xs">
                    {t('enums:ProductUnit.' + p.unit)}
                  </td>
                  <td className="px-4 py-3 t-secondary font-mono text-xs text-right">
                    {formatCents(p.unitPriceCents)}
                  </td>
                  <td className="px-4 py-3 t-secondary font-mono text-xs text-right">
                    {formatBps(p.vatRateBps)}
                  </td>
                  <td className="px-4 py-3 t-secondary font-mono text-xs text-right">
                    {formatBps(p.irpfRateBps)}
                  </td>
                  <td className="px-4 py-3 t-secondary text-xs">
                    {p.serviceLine ? (
                      nameOf(p.serviceLine)
                    ) : (
                      <span className="t-faint">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {p.active ? (
                      <span className="inline-flex items-center gap-1 text-xs text-emerald-700 dark:text-emerald-300">
                        <CircleCheck size={14} /> {t('products.status.active')}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs t-muted">
                        <CircleSlash size={14} /> {t('products.status.inactive')}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      onClick={(e) => onDelete(p, e)}
                      title={t('common:actions.delete')}
                      className="w-8 h-8 grid place-items-center rounded-lg hover:bg-rose-500/10 t-muted hover:text-rose-400"
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <ProductFormModal
        open={showCreate}
        mode="create"
        onClose={() => setShowCreate(false)}
        onSaved={() => {
          setShowCreate(false)
          void reload()
        }}
      />

      <ProductFormModal
        open={editing !== null}
        mode="edit"
        initial={editing ?? undefined}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null)
          void reload()
        }}
      />
    </div>
  )
}

interface ModalProps {
  open: boolean
  mode: 'create' | 'edit'
  initial?: ProductDto
  onClose: () => void
  onSaved: () => void
}

function ProductFormModal({ open, mode, initial, onClose, onSaved }: ModalProps) {
  const { t } = useTranslation(['settings', 'common'])
  const { lines } = useServiceLines()
  const [code, setCode] = useState('')
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [kind, setKind] = useState<ProductKindT>('SERVICIO')
  const [unit, setUnit] = useState<ProductUnitT>('UD')
  const [unitPrice, setUnitPrice] = useState('')
  const [defaultCost, setDefaultCost] = useState('')
  const [vatRateBps, setVatRateBps] = useState<number>(2100)
  const [irpfRateBps, setIrpfRateBps] = useState<number>(0)
  const [serviceLine, setServiceLine] = useState<string>('')
  const [revenueAccountCode, setRevenueAccountCode] = useState('')
  const [position, setPosition] = useState<string>('0')
  const [active, setActive] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    if (mode === 'edit' && initial) {
      setCode(initial.code)
      setName(initial.name)
      setDescription(initial.description ?? '')
      setKind(initial.kind)
      setUnit(initial.unit)
      setUnitPrice(centsToEurosInput(initial.unitPriceCents))
      setDefaultCost(centsToEurosInput(initial.defaultCostCents))
      setVatRateBps(initial.vatRateBps)
      setIrpfRateBps(initial.irpfRateBps)
      setServiceLine(initial.serviceLine ?? '')
      setRevenueAccountCode(initial.revenueAccountCode ?? '')
      setPosition(String(initial.position))
      setActive(initial.active)
    } else {
      setCode('')
      setName('')
      setDescription('')
      setKind('SERVICIO')
      setUnit('UD')
      setUnitPrice('')
      setDefaultCost('')
      setVatRateBps(2100)
      setIrpfRateBps(0)
      setServiceLine('')
      setRevenueAccountCode('')
      setPosition('0')
      setActive(true)
    }
    setError(null)
  }, [open, mode, initial])

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      const positionNum = Number.parseInt(position, 10)
      const payload = {
        code: code.trim(),
        name: name.trim(),
        description: description.trim() ? description.trim() : null,
        kind,
        unit,
        unitPriceCents: parseEurosToCents(unitPrice),
        defaultCostCents: parseEurosToCents(defaultCost),
        vatRateBps,
        irpfRateBps,
        serviceLine: serviceLine === '' ? null : serviceLine,
        revenueAccountCode: revenueAccountCode.trim() ? revenueAccountCode.trim() : null,
        position: Number.isFinite(positionNum) ? positionNum : 0,
        ...(mode === 'edit' ? { active } : {}),
      }
      if (mode === 'create') {
        await api.post('/products', payload)
      } else if (initial) {
        await api.patch(`/products/${initial.id}`, payload)
      }
      onSaved()
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string } | undefined
        setError(body?.error ?? err.message)
      } else {
        setError(err instanceof Error ? err.message : t('common:errors.network'))
      }
    } finally {
      setSubmitting(false)
    }
  }

  const inputCls =
    'w-full px-3 py-2 rounded-lg bg-[var(--bg-input)] border border-[var(--border)] t-primary focus:outline-none focus:ring-1 focus:ring-jr2-500/40 focus:border-jr2-500/60'

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={mode === 'create' ? t('products.form.titleCreate') : t('products.form.titleEdit')}
      maxWidth="max-w-2xl"
    >
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm t-secondary mb-1.5">{t('products.form.code')}</label>
            <input
              required
              minLength={3}
              maxLength={32}
              pattern={PRODUCT_CODE_REGEX}
              title={t('products.form.codeTitle')}
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder={t('products.form.codePlaceholder')}
              className={`${inputCls} font-mono`}
            />
            <p className="text-[11px] t-faint mt-1">{t('products.form.codeHelp')}</p>
          </div>
          <div>
            <label className="block text-sm t-secondary mb-1.5">{t('products.form.name')}</label>
            <input
              required
              minLength={2}
              maxLength={200}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('products.form.namePlaceholder')}
              className={inputCls}
            />
          </div>
        </div>

        <div>
          <label className="block text-sm t-secondary mb-1.5">{t('products.form.description')}</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            maxLength={2000}
            placeholder={t('products.form.descriptionPlaceholder')}
            className={`${inputCls} resize-y`}
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm t-secondary mb-1.5">{t('products.form.kind')}</label>
            <select
              value={kind}
              onChange={(e) => setKind(e.target.value as ProductKindT)}
              className={inputCls}
            >
              {KIND_VALUES.map((k) => (
                <option key={k} value={k}>
                  {t('enums:ProductKind.' + k)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm t-secondary mb-1.5">{t('products.form.unit')}</label>
            <select
              value={unit}
              onChange={(e) => setUnit(e.target.value as ProductUnitT)}
              className={inputCls}
            >
              {UNIT_VALUES.map((u) => (
                <option key={u} value={u}>
                  {t('enums:ProductUnit.' + u)}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm t-secondary mb-1.5">{t('products.form.unitPrice')}</label>
            <input
              inputMode="decimal"
              value={unitPrice}
              onChange={(e) => setUnitPrice(e.target.value)}
              placeholder="0,00"
              className={`${inputCls} font-mono`}
            />
          </div>
          <div>
            <label className="block text-sm t-secondary mb-1.5">{t('products.form.defaultCost')}</label>
            <input
              inputMode="decimal"
              value={defaultCost}
              onChange={(e) => setDefaultCost(e.target.value)}
              placeholder="0,00"
              className={`${inputCls} font-mono`}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm t-secondary mb-1.5">{t('products.form.vat')}</label>
            <select
              value={vatRateBps}
              onChange={(e) => setVatRateBps(Number(e.target.value))}
              className={inputCls}
            >
              {VAT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm t-secondary mb-1.5">{t('products.form.irpf')}</label>
            <select
              value={irpfRateBps}
              onChange={(e) => setIrpfRateBps(Number(e.target.value))}
              className={inputCls}
            >
              {IRPF_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm t-secondary mb-1.5">{t('products.form.serviceLine')}</label>
            <select
              value={serviceLine}
              onChange={(e) => setServiceLine(e.target.value)}
              className={inputCls}
            >
              <option value="">—</option>
              {lines.map((l) => (
                <option key={l.code} value={l.code}>
                  {l.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm t-secondary mb-1.5">{t('products.form.revenueAccount')}</label>
            <input
              maxLength={10}
              value={revenueAccountCode}
              onChange={(e) => setRevenueAccountCode(e.target.value)}
              placeholder="700"
              className={`${inputCls} font-mono`}
            />
            <p className="text-[11px] t-faint mt-1">{t('products.form.revenueAccountHelp')}</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm t-secondary mb-1.5">{t('products.form.position')}</label>
            <input
              type="number"
              min={0}
              step={1}
              value={position}
              onChange={(e) => setPosition(e.target.value)}
              className={`${inputCls} font-mono`}
            />
            <p className="text-[11px] t-faint mt-1">{t('products.form.positionHelp')}</p>
          </div>
          {mode === 'edit' && (
            <div className="flex items-center pt-7">
              <label className="inline-flex items-center gap-2 text-sm t-secondary cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={active}
                  onChange={(e) => setActive(e.target.checked)}
                  className="w-4 h-4 rounded accent-jr2-500 focus:ring-jr2-500/40"
                />
                {t('products.form.active')}
              </label>
            </div>
          )}
        </div>

        {error && (
          <div className="text-sm text-rose-700 dark:text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm bg-[var(--bg-hover)] hover:bg-[var(--bg-input-soft)] t-secondary border border-[var(--border)]"
          >
            {t('common:actions.cancel')}
          </button>
          <button
            type="submit"
            disabled={submitting || code.trim().length < 3 || name.trim().length < 2}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-jr2-600 hover:bg-jr2-700 hover:shadow-glow-jr2 disabled:opacity-50 flex items-center gap-2"
          >
            {submitting && <Loader2 size={14} className="animate-spin" />}
            {mode === 'create' ? t('products.form.submitCreate') : t('products.form.submitEdit')}
          </button>
        </div>
      </form>
    </Modal>
  )
}
